import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DeprecatedConfirmationDialogComponent, DeprecatedModalFormComponent } from '@app/components';
import { AnalyticEvents } from '@app/enums';
import { EmploymentRecordsTable } from '@app/modules/employees/services/tables/employment-records/employment-records.table';
import { RecordForm } from '@app/modules/forms/employees/components';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import { AnalyticService } from '@app/services';
import { AuthService } from '@app/services/auth.service';
import { NotifyService } from '@app/services/notify.service';
import { IndexView } from '@common/views';
import { Employee } from '@models/employee/employee.model';
import { Note } from '@models/employee/note.model';

@Component({
    templateUrl: './records-index.template.html',
    styleUrls: ['./records-index.style.scss'],
})
export class RecordsIndexView extends IndexView implements OnInit {
    @ViewChild('confirmDeleteDialog', { static: true }) confirmDeleteDialog: DeprecatedConfirmationDialogComponent;
    @ViewChild('editModal', { static: true }) editModal: DeprecatedModalFormComponent;
    @ViewChild('editForm', { static: true }) editForm: RecordForm;

    isLoading = true;
    employee: Employee;
    selectedRecord: Note = null;
    createNotePermission: string[] = Note.permission.edit;
    table: EmploymentRecordsTable;

    constructor(
        private route: ActivatedRoute,
        private notify: NotifyService,
        private auth: AuthService,
        protected analyticsService: AnalyticService
    ) {
        super();
        this.employee = this.route.snapshot.data.models.get('employee');
        this.table = new EmploymentRecordsTable(this.auth, this.employee);
    }

    ngOnInit(): void {
        this.isLoading = false;
    }

    menuAction(event: MenuColumnEvent): void {
        const { menuItem, data } = event;
        const employmentRecordsData = data as Note;
        switch (menuItem.event) {
            case 'delete':
                this.onDelete(employmentRecordsData);
                break;
            case 'edit':
                this.editForm.setRecord(employmentRecordsData);
                this.editModal.show();
                break;
        }
    }

    onCreate(record: Note, form?: RecordForm): void {
        if (!record.isDirty()) {
            return;
        }

        const isExistingRecord = Boolean(record.id);

        this.isLoading = true;
        record
            .param('company', this.auth.company.id)
            .param('employee', this.employee.id)
            .save()
            .then(() => {
                if (form) {
                    form.reset();
                }

                // Add Pendo Tracking
                if (isExistingRecord) {
                    this.analyticsService.trackEvent(AnalyticEvents.EmployeeProfileRecordsTabEditRecord);
                } else {
                    this.analyticsService.trackEvent(AnalyticEvents.EmployeeProfileRecordsTabCreateRecord);
                }

                this.notify.success('saved');
                this.isLoading = false;
                this.table.reload();
            })
            .catch((err) => {
                this.notify.error(err.message || 'couldNotSave');
                this.isLoading = false;
            });
    }

    onEdit(record: Note): void {
        this.selectedRecord = record;
    }

    onDelete(record: Note): void {
        this.confirmDeleteDialog.show().then((yes: boolean) => {
            if (yes) {
                this.isLoading = true;
                return record
                    .param('company', this.auth.company.id)
                    .param('employee', this.employee.id)
                    .delete()
                    .then(() => {
                        // Add Pendo Tracking
                        this.analyticsService.trackEvent(AnalyticEvents.EmployeeProfileRecordsTabDeleteRecord);

                        this.notify.success('deleted');
                        this.isLoading = false;
                        this.table.reload();
                    })
                    .catch((err) => {
                        this.isLoading = false;
                        this.notify.error(err.message || 'couldNotDelete');
                    });
            }
        });
    }

    onSelect(record: Note): void {
        this.selectedRecord = record;
    }
}
